/**
 * Authentication API client
 * Handles OAuth login, logout, and current user retrieval
 */

export interface AuthUser {
  authenticated: boolean;
  email?: string;
  provider?: string;
}

export interface ProvidersResponse {
  providers: string[];
}

export interface UserSecret {
  secret_id: string;
  created_at: string;
  expires_at: string;
  ttl: number;
  status: string;
  burn_notification_enabled: boolean;
  burned_at?: string;
  retrieved_at?: string;
}

export interface GetUserSecretsResponse {
  secrets: UserSecret[];
}

export interface DeleteUserDataResponse {
  secrets_deleted: number;
  message: string;
}

/**
 * Get current authenticated user from session cookie
 * Returns user info if authenticated, or { authenticated: false } if not
 */
export async function getCurrentUser(): Promise<AuthUser> {
  try {
    const response = await fetch('/api/auth/user', {
      credentials: 'include', // Include cookies
    });

    if (!response.ok) {
      return { authenticated: false };
    }

    return (await response.json()) as AuthUser;
  } catch (error) {
    console.error('Failed to get current user:', error);
    return { authenticated: false };
  }
}

/**
 * Logout current user by clearing session cookie
 */
export async function logout(): Promise<void> {
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Failed to logout:', error);
  }
}

/**
 * Initiate OAuth login flow by redirecting to provider
 * The browser will be redirected to the OAuth provider's login page
 */
export function loginWithProvider(provider: string): void {
  window.location.href = `/api/auth/login/${provider}`;
}

export interface RequestMagicLinkResponse {
  success: boolean;
  message: string;
}

/**
 * Request a magic link for passwordless email authentication
 * Sends a JWT-based sign-in link to the provided email address
 */
export async function requestMagicLink(email: string): Promise<RequestMagicLinkResponse> {
  const response = await fetch('/api/auth/magic-link/request', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData = (await response.json()) as { error?: string };
    throw new Error(errorData.error ?? `Failed: ${response.status}`);
  }

  return (await response.json()) as RequestMagicLinkResponse;
}

/**
 * Get list of configured OAuth providers
 */
export async function getAvailableProviders(): Promise<string[]> {
  try {
    const response = await fetch('/api/auth/providers', {
      credentials: 'include',
    });

    if (!response.ok) {
      return [];
    }

    const data = (await response.json()) as ProvidersResponse;
    return data.providers || [];
  } catch (error) {
    console.error('Failed to get available providers:', error);
    return [];
  }
}

/**
 * Get all secrets owned by the authenticated user
 * Requires authentication
 */
export async function getUserSecrets(): Promise<UserSecret[]> {
  try {
    const response = await fetch('/api/auth/my-secrets', {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to get user secrets: ${response.status}`);
    }

    const data = (await response.json()) as GetUserSecretsResponse;
    return data.secrets || [];
  } catch (error) {
    console.error('Failed to get user secrets:', error);
    throw error;
  }
}

/**
 * Expire a specific secret owned by the authenticated user
 * Clears encrypted data but keeps the record for audit purposes
 * Requires authentication
 */
export async function expireUserSecret(secretId: string): Promise<void> {
  try {
    const response = await fetch(`/api/auth/my-secrets/${secretId}/expire`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to expire secret: ${response.status}`);
    }
  } catch (error) {
    console.error('Failed to expire secret:', error);
    throw error;
  }
}

/**
 * Delete all user data (GDPR Right to Erasure)
 * Deletes all secrets owned by the authenticated user
 * Requires authentication
 */
export async function deleteAllUserData(): Promise<DeleteUserDataResponse> {
  try {
    const response = await fetch('/api/auth/delete-my-data', {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete user data: ${response.status}`);
    }

    return (await response.json()) as DeleteUserDataResponse;
  } catch (error) {
    console.error('Failed to delete user data:', error);
    throw error;
  }
}
